import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Filter, Search, X } from 'lucide-react';
import React from 'react';

interface DatabaseSearchProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  filterType: string;
  setFilterType: (type: string) => void;
  filterSize: string;
  setFilterSize: (size: string) => void;
  filterStatus: string;
  setFilterStatus: (status: string) => void;
  totalCount: number;
  filteredCount: number;
}

const DatabaseSearch: React.FC<DatabaseSearchProps> = ({
  searchTerm,
  setSearchTerm,
  filterType,
  setFilterType,
  filterSize,
  setFilterSize,
  filterStatus,
  setFilterStatus,
  totalCount,
  filteredCount,
}) => {
  const clearFilters = () => {
    setSearchTerm('');
    setFilterType('all');
    setFilterSize('all');
    setFilterStatus('all');
  };

  const hasActiveFilters = searchTerm || filterType !== 'all' || filterSize !== 'all' || filterStatus !== 'all';

  return (
    <div className="space-y-4">
      {/* Search and Filters */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder="Search by name..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
          />
        </div>

        {/* Database Type Filter */}
        <Select value={filterType} onValueChange={setFilterType}>
          <SelectTrigger>
            <SelectValue placeholder="Type" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Types</SelectItem>
                            <SelectItem value="MYSQL">MySQL</SelectItem>
                <SelectItem value="POSTGRES">PostgreSQL</SelectItem>
                <SelectItem value="MONGODB">MongoDB</SelectItem>
          </SelectContent>
        </Select>

        {/* Size Filter */}
        <Select value={filterSize} onValueChange={setFilterSize}>
          <SelectTrigger>
            <SelectValue placeholder="Size" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Sizes</SelectItem>
            <SelectItem value="1GB">1GB</SelectItem>
            <SelectItem value="5GB">5GB</SelectItem>
            <SelectItem value="10GB">10GB</SelectItem>
            <SelectItem value="50GB">50GB</SelectItem>
            <SelectItem value="100GB">100GB</SelectItem>
          </SelectContent>
        </Select>

        {/* Status Filter */}
        <Select value={filterStatus} onValueChange={setFilterStatus}>
          <SelectTrigger>
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All Status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="pending_delete">Pending Deletion</SelectItem>
          </SelectContent>
        </Select>

        {/* Clear Filters */}
        <Button
          variant="outline"
          onClick={clearFilters}
          disabled={!hasActiveFilters}
        >
          <X className="h-4 w-4 mr-1" />
          Clear
        </Button>
      </div>

      {/* Results Summary */}
      {hasActiveFilters && (
        <div className="flex items-center justify-between">
          <div className="text-sm text-muted-foreground">
            Showing {filteredCount} of {totalCount} databases
          </div>
          {hasActiveFilters && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Filter className="h-4 w-4" />
              <span>Filters active</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default DatabaseSearch; 